import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { RosterPilotLogo } from "../../assets/RosterPilotLogo";
import {
  getPublicFormOrganization,
  submitPlayerForm,
} from "../../services/crmSubmissions";
import type { PlayerSubmissionRequest } from "../../types/submissions";
import { usePlayerFormState } from "../players/playerForm/usePlayerFormState";
import { PlayerFormSections } from "../players/playerForm/PlayerFormSections";
import { PLAYER_FORM_FIELD_LABELS } from "../players/playerForm/playerFormTypes";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import "../players/playerForm/PlayerForm.css";

/**
 * Built from the program name so consent is given to an identified sender, which is what carriers
 * require and what makes the record meaningful. Stored verbatim with the submission: the copy here
 * will change over time, and what a given person agreed to must not change with it.
 */
const smsConsentText = (organizationName: string) =>
  `I agree to receive recruiting text messages from ${organizationName} at the phone number ` +
  `provided. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP ` +
  `for help.`;

export const PlayerSubmissionForm = () => {
  const { orgToken } = useParams<{ orgToken: string }>();
  const [submitted, setSubmitted] = useState(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  /**
   * Counter rather than a boolean so a second failed submit re-fires the focus
   * effect. A boolean would already be true and the summary would never be
   * re-announced.
   */
  const [failedAttempts, setFailedAttempts] = useState(0);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);

  useDocumentTitle("Player Interest Form");

  const { state, setters, errors, validate, buildPayload } = usePlayerFormState();

  useEffect(() => {
    if (!orgToken) return;
    // Best effort. Without the name the consent line falls back to generic wording rather than
    // blocking the form, but the named version is the one carriers want to see.
    getPublicFormOrganization(orgToken)
      .then((org) => setOrganizationName(org.organizationName))
      .catch(() => setOrganizationName(null));
  }, [orgToken]);

  const consentSentence = smsConsentText(
    organizationName ?? "the program you are contacting"
  );

  const failedFields = Object.entries(errors).filter(([, message]) => Boolean(message));

  useEffect(() => {
    if (failedAttempts > 0) errorSummaryRef.current?.focus();
  }, [failedAttempts]);

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setFailedAttempts((n) => n + 1);
      return;
    }
    setFailedAttempts(0);
    setSubmitError("");
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const request: PlayerSubmissionRequest = {
        ...payload,
        // intake form does not collect recruitment stage
        recruitmentStage: undefined,
        smsConsent,
        // Sent even when unticked is false, so the stored record is of what was shown.
        smsConsentText: consentSentence,
      };
      await submitPlayerForm(orgToken!, request);
      setSubmitted(true);
    } catch (err: unknown) {
      setSubmitError(
        err instanceof Error ? err.message : "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="submission-screen">
        <div className="submission-container">
          <div className="submission-logo">
            <RosterPilotLogo />
          </div>
          <div className="submission-success" role="status">
            {/*
             * Focused on mount so a screen reader lands on the confirmation
             * rather than back at the top of a page whose form just vanished.
             */}
            <h1 className="submission-success__title" tabIndex={-1} ref={successRef}>
              Thank You!
            </h1>
            <p className="submission-success__message">
              We received your submission and will be in touch soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="submission-screen">
      <div className="submission-container">
        <div className="submission-logo">
          <RosterPilotLogo />
        </div>
        <div className="submission-header">
          <h1 className="submission-header__title">Player Interest Form</h1>
          <p className="submission-header__subtitle">
            {organizationName
              ? `Submit your information and the staff at ${organizationName} will review your profile.`
              : "Submit your information and our staff will review your profile."}
          </p>
        </div>

        <form className="submission-form" onSubmit={handleSubmit} noValidate>
          {submitError && (
            <div className="submission-error-banner" role="alert">
              {submitError}
            </div>
          )}

          {failedAttempts > 0 && failedFields.length > 0 && (
            <div
              className="submission-error-summary"
              role="alert"
              tabIndex={-1}
              ref={errorSummaryRef}
            >
              <h2 className="submission-error-summary__title">
                {failedFields.length === 1
                  ? "1 field needs your attention"
                  : `${failedFields.length} fields need your attention`}
              </h2>
              <ul className="submission-error-summary__list">
                {failedFields.map(([field, message]) => (
                  <li key={field}>
                    {PLAYER_FORM_FIELD_LABELS[field] ?? field}: {message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <PlayerFormSections
            state={state}
            setters={setters}
            errors={errors}
            disabled={submitting}
            showRecruitmentStage={false}
          />

          {/*
            Unticked by default and never required. Consent that a form forces you to give is not
            consent, and carriers treat a pre-ticked or mandatory box as invalid. A player who
            declines still gets submitted; the program simply cannot text them.
          */}
          <div className="submission-consent">
            <label className="submission-consent__label">
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                disabled={submitting}
              />
              <span className="submission-consent__text">{consentSentence}</span>
            </label>
            <p className="submission-consent__links">
              See our{" "}
              <a
                href="https://www.rosterpilot.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://www.rosterpilot.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              . Leaving this unticked does not stop you submitting the form.
            </p>
          </div>

          <div className="submission-actions">
            <Button type="submit" fullWidth loading={submitting}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
